import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { IDM_CREATE_ROUTE } from "../../../../app/router/routes";
import { getStaffDetailPath, getStaffListPath, isTutorStaffItem, withStaffReturnPath } from '../../../../app/router/staffNavigation';
import { useAppSelector } from "../../../../app/store/store";
import { IIdmRole, IIdmStaffListItem, idmService } from "../../../../entities/idm";
import { FEATURES, hasFeature } from "../../../../entities/my";
import { getStaffPermissions } from "../../model/getStaffPermissions";
import { IStaffWidgetProps } from "../../model/types";
import { useStaffFeedback } from "../../model/useStaffFeedback";
import { StaffListContent } from "../staffList/StaffListContent";

export type {
    IStaffCardWidgetProps,
    IStaffProfileSectionsContext,
    IStaffTabActions,
    IStaffWidgetProps,
    IStaffWorkTgFilterContext,
} from "../../model/types";

const getSearchStaffItems = (items: IIdmStaffListItem[]) => items.map(item => ({
    ...item,
    name: `${item.full_name} ${item.email} ${item.roles.join(' ')} ${item.user_id}`,
}))

const isTutorRole = (role: IIdmRole) => {
    const roleName = role.name.trim().toLowerCase()
    const roleCode = role.code.trim().toLowerCase()
    return roleCode === 'tutor' || roleName.includes('репетитор') || roleName.includes('tutor')
}

export const StaffWidget: FC<IStaffWidgetProps> = ({renderWorkTgFilter, section, archived}) => {
    const navigate = useNavigate()
    const location = useLocation()
    const [params, setParams] = useSearchParams()
    const {my} = useAppSelector(state => state.myReducer)
    const permissions = getStaffPermissions(my)
    const isPerformer = section === 'performers'
    const listPath = getStaffListPath(section, archived)
    const {handleError} = useStaffFeedback()
    const canUseWithoutTgFilter = ['admin', 'super_admin', 'assistant', 'hr'].includes(my.role)
    const roleIdFromParams = Number(params.get('role_id'))
    const roleId = isPerformer || Number.isNaN(roleIdFromParams) || roleIdFromParams <= 0 ? null : roleIdFromParams
    const workTgIdsFromParams = params
        .getAll('work_tg_ids')
        .map(Number)
        .filter(id => !Number.isNaN(id) && id > 0)
    const workTgIdsKey = workTgIdsFromParams.join(',')
    const tagIdsKey = params
        .getAll('tag_ids')
        .map(Number)
        .filter(id => !Number.isNaN(id) && id > 0)
        .join(',')
    const selectedTagIds = useMemo(
        () => tagIdsKey ? tagIdsKey.split(',').map(Number) : [],
        [tagIdsKey]
    )
    const staffArchiveFromParams = !isPerformer && archived
    const archiveFilterFromParams = isPerformer && archived
    const withoutTgFromParams = params.get('without_tg') === 'true'
    const isLowLoadFromParams = params.get('is_low_load') === 'true'
    const tutorLabelsFromParams = params.getAll('labels')
    const shouldResetFilters = Boolean((location.state as {resetFilters?: boolean} | null)?.resetFilters)

    const [isLoading, setIsLoading] = useState(true)
    const [staff, setStaff] = useState<IIdmStaffListItem[]>([])
    const [roles, setRoles] = useState<IIdmRole[]>([])
    const searchValue = params.get('q') || ''
    const setSearchValue = (value: string) => setParams(current => {
        const next = new URLSearchParams(current)
        value ? next.set('q', value) : next.delete('q')
        return next
    }, {replace: true})
    const [selectedWorkTgIds, setSelectedWorkTgIds] = useState<number[]>(workTgIdsFromParams)
    const [tutorCount, setTutorCount] = useState(0)
    const selectedRoleId = roleId
    const isStaffArchiveTab = staffArchiveFromParams
    const isTutorArchiveFilter = archiveFilterFromParams
    const isWithoutTgFilter = withoutTgFromParams

    const tutorRole = roles.find(isTutorRole) || null
    const workerRoles = useMemo(() => roles.filter(role => !isTutorRole(role)), [roles])
    const isTutorRoleSelected = isPerformer
    const effectiveWorkTgIds = useMemo(
        () => permissions.canReadWorkTgs ? selectedWorkTgIds : [],
        [permissions.canReadWorkTgs, selectedWorkTgIds]
    )

    const loadStaff = useCallback(async (selectedRole: number | null, workTgIds: number[], withoutTg: boolean, isArchive: boolean, tagIds: number[]) => {
        try{
            setIsLoading(true)
            const [staffResponse, rolesResponse] = await Promise.allSettled([
                isArchive
                    ? idmService.listArchivedStaff(tagIds)
                    : idmService.listStaff(selectedRole ? [selectedRole] : [], workTgIds, withoutTg, tagIds),
                permissions.canListRoles ? idmService.listRoles() : Promise.resolve([]),
            ])

            if(staffResponse.status !== 'fulfilled'){
                throw staffResponse.reason
            }

            setStaff(staffResponse.value.items)

            if(rolesResponse.status === 'fulfilled'){
                setRoles(rolesResponse.value)
            }
        }
        catch(error){
            handleError(error, 'Ошибка при получении списка сотрудников')
        }
        finally{
            setIsLoading(false)
        }
    }, [handleError, permissions.canListRoles])

    useEffect(() => {
        if(!shouldResetFilters){
            return
        }

        setSelectedWorkTgIds([])
        navigate({pathname: listPath, search: ''}, {replace: true, state: null})
    }, [navigate, shouldResetFilters, listPath])

    useEffect(() => {
        if(isPerformer) return
        const legacyTutor = roleId !== null && roleId === tutorRole?.id
        if(legacyTutor || (!archived && params.get('archive_scope') === 'staff')){
            const next = new URLSearchParams(params)
            next.delete('archive_scope')
            if(legacyTutor) next.delete('role_id')
            const nextArchived = legacyTutor ? next.get('is_archive') === 'true' : true
            next.delete('is_archive')
            navigate({pathname: getStaffListPath(legacyTutor ? 'performers' : 'workers', nextArchived), search: next.toString()}, {replace: true})
        }
    }, [archived, isPerformer, navigate, params, roleId, tutorRole?.id])

    useEffect(() => {
        const nextWorkTgIds = workTgIdsKey ? workTgIdsKey.split(',').map(Number) : []
        setSelectedWorkTgIds(current => {
            const isEqual = current.length === nextWorkTgIds.length
                && current.every((id, index) => id === nextWorkTgIds[index])
            return isEqual ? current : nextWorkTgIds
        })
    }, [workTgIdsKey])

    useEffect(() => {
        if(isPerformer || shouldResetFilters || !permissions.isListAvailable){
            setIsLoading(false)
            return
        }

        loadStaff(selectedRoleId, effectiveWorkTgIds, isWithoutTgFilter, isStaffArchiveTab, selectedTagIds)
    }, [isPerformer, effectiveWorkTgIds, isStaffArchiveTab, isWithoutTgFilter, loadStaff, permissions.isListAvailable, selectedRoleId, shouldResetFilters, selectedTagIds])

    const filteredStaff = useMemo(() => {
        const search = searchValue.trim().toLowerCase()
        let searchStaff = getSearchStaffItems(staff)
            .filter(item => !isTutorStaffItem(item))

        if(isStaffArchiveTab && selectedRoleId !== null){
            const selectedRole = roles.find(role => role.id === selectedRoleId)
            if(selectedRole){
                searchStaff = searchStaff.filter(item => item.roles.includes(selectedRole.name))
            }
        }

        return search ? searchStaff.filter(item => item.name.toLowerCase().includes(search)) : searchStaff
    }, [isStaffArchiveTab, roles, searchValue, selectedRoleId, staff])

    const staffEmptyText = useMemo(() => {
        if(isStaffArchiveTab){
            return searchValue.trim() || selectedRoleId !== null
                ? 'Работники в архиве по выбранным фильтрам не найдены'
                : 'Архив работников пуст'
        }
        if(searchValue.trim() || effectiveWorkTgIds.length > 0 || selectedRoleId !== null || isWithoutTgFilter || selectedTagIds.length > 0){
            return 'Работники по выбранным фильтрам не найдены'
        }
        return 'Список работников пуст'
    }, [effectiveWorkTgIds, isStaffArchiveTab, isWithoutTgFilter, searchValue, selectedRoleId, selectedTagIds.length])

    const onSelectRole = (nextRoleId: number | null) => {
        setParams(currentParams => {
            const nextParams = new URLSearchParams(currentParams)
            if(nextRoleId === null){
                nextParams.delete('role_id')
                nextParams.delete('is_archive')
                nextParams.delete('is_failer')
            }
            else{
                nextParams.set('role_id', String(nextRoleId))
            }
            return nextParams
        })
    }

    const onWorkTgChange = (workTgIds: number[]) => {
        const normalizedIds = Array.from(new Set(workTgIds)).sort((first, second) => first - second)
        setSelectedWorkTgIds(normalizedIds)

        setParams(currentParams => {
            const nextParams = new URLSearchParams(currentParams)
            nextParams.delete('work_tg_ids')
            normalizedIds.forEach(id => nextParams.append('work_tg_ids', String(id)))
            return nextParams
        })
    }

    const onTagIdsChange = (tagIds: number[]) => {
        const normalizedIds = Array.from(new Set(tagIds)).filter(id => id > 0).sort((first, second) => first - second)
        setParams(currentParams => {
            const nextParams = new URLSearchParams(currentParams)
            nextParams.delete('tag_ids')
            normalizedIds.forEach(id => nextParams.append('tag_ids', String(id)))
            return nextParams
        })
    }

    const onWithoutTgChange = (value: boolean) => {
        setParams(currentParams => {
            const nextParams = new URLSearchParams(currentParams)
            if(value){
                nextParams.set('without_tg', 'true')
            }
            else{
                nextParams.delete('without_tg')
            }
            return nextParams
        })
    }

    const onTutorLabelsChange = (labels: string[]) => {
        setParams(currentParams => {
            const nextParams = new URLSearchParams(currentParams)
            nextParams.delete('labels')
            labels.forEach(label => nextParams.append('labels', label))
            return nextParams
        })
    }

    const onLowLoadChange = (value: boolean) => {
        setParams(currentParams => {
            const nextParams = new URLSearchParams(currentParams)
            if(value){
                nextParams.set('is_low_load', 'true')
            }
            else{
                nextParams.delete('is_low_load')
            }
            return nextParams
        })
    }

    const getStaffPath = (userId: number | null) => {
        return userId
            ? withStaffReturnPath(getStaffDetailPath(userId, section), `${listPath}${location.search}`)
            : listPath
    }

    const tabActions = {
        selectAll: () => {
            setTutorCount(0)
            navigate({pathname: getStaffListPath(section), search: ''}, {replace: false})
        },
        selectRole: (nextRoleId: number) => {
            const nextParams = new URLSearchParams(params)
            nextParams.set('role_id', String(nextRoleId))
            navigate({pathname: listPath, search: `?${nextParams.toString()}`}, {replace: false})
        },
        selectStaffArchive: () => {
            setTutorCount(0)
            const nextParams = new URLSearchParams(params)
            nextParams.delete('archive_scope')
            nextParams.delete('is_archive')
            nextParams.delete('is_failer')
            nextParams.delete('work_tg_ids')
            nextParams.delete('without_tg')
            navigate({pathname: getStaffListPath(section, true), search: `?${nextParams.toString()}`}, {replace: false})
        },
    }

    return (
        <StaffListContent
            visible={permissions.isListAvailable}
            canCreateStaff={permissions.canCreateStaff}
            canListRoles={permissions.canListRoles}
            canReadWorkTgs={permissions.canReadWorkTgs}
            isTutorRoleSelected={isTutorRoleSelected}
            isTutorListOnlyMode={isPerformer}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            selectedWorkTgIds={selectedWorkTgIds}
            onWorkTgChange={onWorkTgChange}
            selectedTagIds={selectedTagIds}
            onTagIdsChange={onTagIdsChange}
            renderWorkTgFilter={renderWorkTgFilter}
            selectedRoleId={selectedRoleId}
            roles={workerRoles}
            tutorRoleId={null}
            tutorCount={tutorCount}
            tabActions={tabActions}
            onSelectRole={onSelectRole}
            isTutorArchiveFilter={isTutorArchiveFilter}
            setIsTutorArchiveFilter={(value) => {
                navigate({pathname: getStaffListPath(section, value), search: location.search})
            }}
            isStaffArchiveTab={isStaffArchiveTab}
            setIsStaffArchiveTab={() => undefined}
            canUseWithoutTgFilter={canUseWithoutTgFilter}
            isWithoutTgFilter={isWithoutTgFilter}
            setIsWithoutTgFilter={onWithoutTgChange}
            tutorLabels={tutorLabelsFromParams}
            onTutorLabelsChange={onTutorLabelsChange}
            isLowLoadFilter={isLowLoadFromParams}
            setIsLowLoadFilter={onLowLoadChange}
            isLoading={isLoading}
            effectiveWorkTgIds={effectiveWorkTgIds}
            getStaffPath={getStaffPath}
            setTutorCount={setTutorCount}
            filteredStaff={filteredStaff}
            staffEmptyText={staffEmptyText}
            canOpenStaff={currentUserId => hasFeature(my, FEATURES.IDM.STAFF_READ, currentUserId)}
            onCreateStaff={() => navigate(withStaffReturnPath(IDM_CREATE_ROUTE.path, `${listPath}${location.search}`))}
        />
    )
}
